/**
 * One person's day as a sequence of observed-activity blocks.
 *
 * The rollup endpoint drops time-of-day (it keeps `ts[:10]` as the day bucket),
 * so the Timeline can't come from `/auth/admin/user-activity`. It comes from
 * its sibling instead — `GET /auth/admin/user-activity/timeline?user=&date=`
 * (user-activity-timeline-complete.md), which returns EVERY structured audit
 * event that actor produced on that New York day, across both pipelines,
 * whether or not the loan still exists. One request per person per day; no
 * loan cap, no pagination, no client-side join.
 *
 * ── What this replaced, and why ────────────────────────────────────────────
 * Until 2026-09-18 the sessions were rebuilt from the per-loan feed:
 * `loans_touched` → cached preclose pipeline → `GET /underwriting/loan_events`
 * for the first FIVE resolvable deals. That was a sample presented as a day —
 * acquisitions never resolved, archived loans vanished, and because
 * `loans_touched` sorts ascending the cap threw away the NEWEST loans first,
 * i.e. the ones the person had just created. On 2026-09-17 it drew 262 of 530
 * events and 10 of 22 creations. Nothing from that join survives here:
 * `loans_touched` is not a timeline data source, and adjacent rollup rows are
 * never used to decide what is allowed onto the strip.
 *
 * ── Contract points that shape this hook ───────────────────────────────────
 * - The day is the backend's: it filters the exact half-open Eastern interval
 *   (DST-aware). Events are NOT re-filtered here by `timestamp[:10]`.
 * - A failed request is an error the row can retry from. It is never cached as
 *   an empty successful day, and there is no fallback to the old sample.
 * - An empty day is a valid response with `complete: true` and `events: []`.
 *   A response without `complete: true` is treated as a failure — the strip
 *   would otherwise draw a partial day as the whole one.
 * - Refresh clears every cached user/day and ignores whatever was in flight
 *   (generation counter): a stale response must not overwrite a refreshed day.
 * - Loading many rows runs at most four requests at once.
 */
import { useCallback, useRef, useState } from "react";

import { adminUserActivityApi } from "../../../api/adminPlatformApi";
import { buildSessions, timelineEvents } from "./userActivityModel";

// How many people's days to have in flight at once from "Load the other N".
const CONCURRENCY = 4;

const entryKey = (entry) => `${entry?.user}|${entry?.date}`;

const runPooled = async (items, worker) => {
  const queue = [...items];
  const runners = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
    while (queue.length) {
      const item = queue.shift();
      // eslint-disable-next-line no-await-in-loop
      await worker(item);
    }
  });
  await Promise.all(runners);
};

export const useUserActivitySessions = () => {
  const [results, setResults] = useState({});
  const resultsRef = useRef(results);
  resultsRef.current = results;

  const inFlight = useRef(new Set()); // rowKey
  // Bumped by `clear()`. A response whose generation is older than the current
  // one belongs to a day that has since been refreshed and is dropped.
  const generation = useRef(0);

  /**
   * Load one person's Eastern day. `entry.user` + `entry.date` (YYYY-MM-DD) are
   * all the request needs; `entry.sourceRows` (the rollup rows that nominated
   * this person/day) is deliberately NOT read here.
   */
  const load = useCallback(async (entry) => {
    if (!entry?.user || !entry?.date) return;
    const key = entryKey(entry);
    if (inFlight.current.has(key)) return;
    const prior = resultsRef.current[key];
    if (prior && prior.status === "ready") return;

    const gen = generation.current;
    inFlight.current.add(key);
    setResults((r) => ({ ...r, [key]: { status: "loading" } }));

    try {
      const data = await adminUserActivityApi.timeline({
        user: entry.user,
        date: entry.date,
      });
      if (gen !== generation.current) return; // refreshed while in flight
      if (!data || data.complete !== true || !Array.isArray(data.events)) {
        setResults((r) => ({
          ...r,
          [key]: {
            status: "error",
            reason: "The backend returned an incomplete day.",
          },
        }));
        return;
      }
      const events = timelineEvents(data.events);
      const sessions = buildSessions(events);
      setResults((r) => ({
        ...r,
        [key]: {
          status: "ready",
          sessions,
          // Totals come from the complete response, never from the rollup rows
          // — those are UTC days and this is a New York one.
          edits: sessions.reduce((n, x) => n + x.edits, 0),
          views: sessions.reduce((n, x) => n + x.views, 0),
          totalEvents: Number(data.total_events) || events.length,
          loanCount: Number(data.loan_count) || 0,
          categories: data.categories || {},
          coverage: data.coverage || "",
          startUtc: data.startUtc || null,
          endUtc: data.endUtc || null,
        },
      }));
    } catch (err) {
      if (gen !== generation.current) return;
      setResults((r) => ({
        ...r,
        [key]: {
          status: "error",
          reason:
            err?.response?.data?.detail ||
            err?.message ||
            "Couldn't read this day's events.",
        },
      }));
    } finally {
      inFlight.current.delete(key);
    }
  }, []);

  const loadMany = useCallback(
    (entries) => {
      runPooled(entries, load);
    },
    [load],
  );

  const resultFor = useCallback(
    (entry) => results[entryKey(entry)] || { status: "idle" },
    [results],
  );

  /** Forget every cached day and disown anything still in flight. */
  const clear = useCallback(() => {
    generation.current += 1;
    inFlight.current.clear();
    setResults({});
  }, []);

  /** One request per person/day — the number the "Load the other N" button quotes. */
  const requestCost = useCallback(
    (entries) =>
      entries.filter((e) => {
        const r = resultsRef.current[entryKey(e)];
        return !r || r.status === "idle" || r.status === "error";
      }).length,
    [],
  );

  return { load, loadMany, resultFor, requestCost, clear };
};

export default useUserActivitySessions;
